import { useEffect, useState } from 'preact/hooks';
import {
  COST_GROUPS,
  COST_KEYS,
  COST_LABELS,
  costStatus,
  kr,
  STANDARD_TRAVEL,
  travelSummary,
  type AgreementCosts,
  type AgreementPackagePrice,
  type BasisLine,
  type CostKey,
  type CostStatus,
  type TravelIncluded,
  type TravelRule
} from '@kff/domain';
import { ApiError, type Api } from '../api.js';
import { dm, dmSv } from '../format.js';
import { texts, useLang, type Lang } from '../i18n.js';
import { Block, Dialog, LoadError, Loading, Pill, toast } from '../ui.js';

/**
 * The client's agreement as figures (owner 2026-10-29, migration 0060): Klara reads Monday's agreement PDFs, active clients
 * first, and a producer or admin corrects and approves each before the figures are used ("Aktiva kunder först, med
 * granskning"). Finance › Agreement terms is the review list and the switch; the client card shows what applies.
 */

type Status = 'queued' | 'proposed' | 'approved' | 'rejected' | 'failed';
export interface TermsRow {
  id: string;
  clientId: string;
  clientName: string;
  clientActive: boolean;
  fileName: string;
  fileDate: string | null;
  url: string | null;
  status: Status;
  costs: AgreementCosts | null;
  quotes: Partial<Record<CostKey, string>>;
  found: number;
  summary: string | null;
  error: string | null;
}
export interface ClientCostsView {
  source: 'quote' | 'scan' | 'standard';
  ref: string | null;
  costs: AgreementCosts;
  from: Record<CostKey, 'agreement' | 'standard'>;
}
interface ListView {
  on: boolean;
  configured: boolean;
  folderOn: boolean;
  counts: {
    queued: number;
    queuedActive: number;
    proposed: number;
    approved: number;
    rejected: number;
    failed: number;
    total: number;
  };
  toReview: TermsRow[];
  standard: AgreementCosts;
}

const TT = texts(
  {
    head: 'Agreement terms',
    sub: 'Klara reads the agreements from Monday, active clients first. Check each against the agreement and approve; only approved figures are used on the client and its invoice basis.',
    on: 'Reading agreements',
    off: 'Not reading agreements',
    start: 'Start reading',
    stop: 'Pause',
    notConfigured: 'Klara and Drive need GOOGLE_CALENDAR_SA_KEY.',
    noFolder: 'Turn on the agreements folder in Settings first: the agreements are read from Drive.',
    counts: (c: ListView['counts']) => `${c.proposed} to review · ${c.queued} waiting (${c.queuedActive} active clients) · ${c.approved} approved · ${c.failed} could not be read`,
    nothing: 'Nothing to review right now.',
    review: 'Review',
    found: (n: number) => `${n} figures found`,
    inactive: 'not active',
    status: {
      queued: 'Waiting',
      proposed: 'To review',
      approved: 'Approved',
      rejected: 'Set aside',
      failed: 'Could not be read'
    } as Record<Status, string>,
    dialog: (c: string) => `Agreement terms · ${c}`,
    open: 'Open the agreement',
    fromAgreement: 'From the agreement:',
    notFound: 'Not in the agreement: the standard applies.',
    approve: 'Approve',
    reject: 'Not this client’s agreement',
    rescan: 'Read again',
    approved: 'Approved: the figures now apply to the client',
    rejected: 'Set aside',
    rescanned: 'Klara reads it again on the next run',
    cancel: 'Cancel',
    travel: {
      stockholm: 'Inside Stockholm included',
      all: 'All travel included',
      none: 'All travel invoiced'
    } as Record<TravelIncluded, string>,
    share: 'Travel time, % of the hourly price',
    ownRate: 'Own hourly price for travel time',
    none: '–',
    frame: 'Frame agreement, no minimum',
    minimum: (n: number, m: number | null) => `At least ${n} ${n === 1 ? 'production' : 'productions'}${m ? ` within ${m} months` : ''}`,
    // the client card
    cardHead: 'Agreement terms',
    srcQuote: (r: string) => `From agreement ${r}`,
    srcScan: (r: string) => `From the Monday agreement ${r}`,
    srcStandard: 'No agreement read yet: the standard applies',
    standardTag: 'standard',
    read: 'Agreements read',
    units: { months: 'months', days: 'days', x: '×' } as Record<string, string>,
    progress: (i: number, n: number) => `${i} of ${n} to review`,
    klara: 'Klara’s summary',
    pill: {
      standard: 'As the price list',
      differs: 'Differs',
      found: 'Found',
      missing: 'Not stated'
    } as Record<CostStatus, string>,
    priceSays: (v: string) => `The price list says ${v}`,
    clickHint: 'Click a figure to change it.',
    silent: 'The agreement says nothing about:',
    silentEnd: 'The standard applies.',
    next: 'Correct, next agreement →',
    last: 'Correct, approve',
    more: 'More options',
    done: 'All agreements reviewed',
    perHour: (f: string) => `(${f} the hourly price)`,
    called: (done: number, min: number) => `${done} of ${min} productions ordered`,
    left: (n: number, end: string) => `${n} left before ${end}`,
    reached: 'The minimum is reached',
    shortfall: 'What is left is invoiced when the period ends.',
    until: (d: string) => `until ${d}`,
    length: (m: number) => `${m} months`,
    extra: 'What costs extra',
    showAll: 'Show all terms',
    showLess: 'Show fewer',
    approvedOn: (d: string) => `approved ${d}`,
    change: 'Change',
    pkgName: 'Package',
    pkgPrice: 'Price, kr',
    pkgRemove: 'Remove',
    pkgAdd: 'Add a package'
  },
  {
    head: 'Avtalsvillkor',
    sub: 'Klara läser avtalen från Monday, aktiva kunder först. Kontrollera mot avtalet och godkänn. Bara godkända siffror används på kunden och i fakturaunderlaget.',
    on: 'Läser avtal',
    off: 'Läser inte avtal',
    start: 'Börja läsa',
    stop: 'Pausa',
    notConfigured: 'Klara och Drive behöver GOOGLE_CALENDAR_SA_KEY.',
    noFolder: 'Slå först på avtalsmappen i Inställningar: avtalen läses från Drive.',
    counts: (c: ListView['counts']) => `${c.proposed} att granska · ${c.queued} väntar (${c.queuedActive} aktiva kunder) · ${c.approved} godkända · ${c.failed} gick inte att läsa`,
    nothing: 'Inget att granska just nu.',
    review: 'Granska',
    found: (n: number) => `${n} värden hittade`,
    inactive: 'inte aktiv',
    status: {
      queued: 'Väntar',
      proposed: 'Att granska',
      approved: 'Godkänt',
      rejected: 'Bortvalt',
      failed: 'Gick inte att läsa'
    },
    dialog: (c: string) => `Avtalsvillkor · ${c}`,
    open: 'Öppna avtalet',
    fromAgreement: 'Ur avtalet:',
    notFound: 'Står inte i avtalet: standard gäller.',
    approve: 'Godkänn',
    reject: 'Inte kundens avtal',
    rescan: 'Läs igen',
    approved: 'Godkänt: siffrorna gäller nu för kunden',
    rejected: 'Bortvalt',
    rescanned: 'Klara läser det igen vid nästa körning',
    cancel: 'Avbryt',
    travel: {
      stockholm: 'Inom Stockholm ingår',
      all: 'Alla resor ingår',
      none: 'Alla resor debiteras'
    },
    share: 'Restid, % av timpriset',
    ownRate: 'Eget timpris för restid',
    none: '–',
    frame: 'Ramavtal utan minimum',
    minimum: (n: number, m: number | null) => `Minst ${n} ${n === 1 ? 'produktion' : 'produktioner'}${m ? ` inom ${m} mån` : ''}`,
    cardHead: 'Avtalsvillkor',
    srcQuote: (r: string) => `Från avtal ${r}`,
    srcScan: (r: string) => `Från Monday-avtalet ${r}`,
    srcStandard: 'Inget avtal inläst än: standard gäller',
    standardTag: 'standard',
    read: 'Inlästa avtal',
    units: { months: 'mån', days: 'dagar', x: '×' },
    progress: (i: number, n: number) => `${i} av ${n} att granska`,
    klara: 'Klaras sammanfattning',
    pill: {
      standard: 'Som prislistan',
      differs: 'Avviker',
      found: 'Hittat',
      missing: 'Står inte'
    },
    priceSays: (v: string) => `Prislistan säger ${v}`,
    clickHint: 'Klicka på en siffra för att ändra den.',
    silent: 'Avtalet säger inget om:',
    silentEnd: 'Standard gäller.',
    next: 'Stämmer, nästa avtal →',
    last: 'Stämmer, godkänn',
    more: 'Fler val',
    done: 'Alla avtal granskade',
    perHour: (f: string) => `(${f} timpriset)`,
    called: (done: number, min: number) => `${done} av ${min} produktioner avropade`,
    left: (n: number, end: string) => `${n} kvar före ${end}`,
    reached: 'Minimum är uppnått',
    shortfall: 'Det som återstår faktureras när perioden slutar.',
    until: (d: string) => `till ${d}`,
    length: (m: number) => `${m} mån`,
    extra: 'Det här kostar extra',
    showAll: 'Visa alla villkor',
    showLess: 'Visa färre',
    approvedOn: (d: string) => `godkänt ${d}`,
    change: 'Ändra',
    pkgName: 'Paket',
    pkgPrice: 'Pris, kr',
    pkgRemove: 'Ta bort',
    pkgAdd: 'Lägg till paket'
  }
);

const msg = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : String(e));
/** A date, with the year when it is not this year (agreements run over years). */
export const day = (d: string | null, lang: Lang) => {
  if (!d) return '';
  const k = d.slice(0, 10);
  const s = lang === 'sv' ? dmSv(k) : dm(k);
  return k.slice(0, 4) === String(new Date().getFullYear()) ? s : `${s} ${k.slice(0, 4)}`;
};
const tone = (s: Status): 'ok' | 'warn' | 'crit' | 'neutral' | 'info' => (s === 'approved' ? 'ok' : s === 'proposed' ? 'warn' : s === 'failed' ? 'crit' : s === 'rejected' ? 'neutral' : 'info');

/** One figure as text, with its unit. */
/** The scan's errors are written in English (the cron has no language); the known ones in Swedish. */
const ERRORS_SV: [RegExp, (m: RegExpMatchArray) => string][] = [
  [/^the file is not in Drive yet/, () => 'Filen finns inte i Drive än.'],
  [/^not a PDF/, () => 'Inte en PDF: öppna avtalet och fyll i siffrorna själv.'],
  [/^Drive answered (\d+)/, (m) => `Drive svarade ${m[1]}.`],
  [/^too big to read/, () => 'För stor för Klara (över 10 MB): fyll i siffrorna själv.'],
  [/^Klara’s answer was cut short/, () => 'Klaras svar blev avbrutet. Läs igen.'],
  [/^Klara did not answer with figures/, () => 'Klara svarade inte med siffror. Läs igen eller fyll i själv.']
];
function errorText(e: string | null, lang: Lang): string {
  if (!e || lang !== 'sv') return e ?? '';
  for (const [re, sv] of ERRORS_SV) {
    const m = e.match(re);
    if (m) return sv(m);
  }
  return e;
}

export function costText(k: CostKey, c: AgreementCosts, lang: Lang): string {
  const v = c[k];
  if (v === null || v === undefined) return TT[lang].none;
  if (k === 'travel') return travelSummary(v as TravelRule, c.hourlyRate ?? 1500, lang);
  if (k === 'packages') return (v as AgreementPackagePrice[]).map((x) => `${x.key} ${kr(x.price)}`).join(' · ');
  if (k === 'minProductions') return v === 0 ? TT[lang].frame : TT[lang].minimum(v as number, c.periodMonths);
  if (k === 'untilDate') return day(v as string, lang);
  if (typeof v === 'string') return v;
  const u = COST_LABELS[k].unit;
  const n = v as number;
  return u === 'kr' ? kr(n) : u === 'kr/h' ? `${kr(n)}/h` : u === '%' ? `${n} %` : u === 'x' ? `${String(n).replace('.', ',')} ×` : u ? `${n} ${TT[lang].units[u] ?? u}` : String(n);
}

/** Finance › Agreement terms: the switch, how far it has come, and the agreements to review. */
export function TermsReview(p: { api: Api }) {
  const lang = useLang();
  const t = TT[lang];
  const [v, setV] = useState<ListView | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [open, setOpen] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const load = () => p.api.get<ListView>('/api/finance/terms').then(setV, setErr);
  useEffect(() => void load(), []);
  if (err) return <LoadError error={err} />;
  if (!v) return <Loading />;
  const toggle = async () => {
    setBusy(true);
    try {
      setV(await p.api.post<ListView>('/api/finance/terms/scan', { on: !v.on }));
    } catch (e) {
      toast({ text: msg(e) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Block title={t.head}>
        <p class="hint">{t.sub}</p>
        <p class="cluster">
          <Pill tone={v.on ? 'ok' : 'neutral'}>{v.on ? t.on : t.off}</Pill>
          <button type="button" class="btn btn-sm" disabled={busy || (!v.on && (!v.configured || !v.folderOn))} onClick={() => void toggle()}>
            {v.on ? t.stop : t.start}
          </button>
        </p>
        {!v.configured ? <p class="small contract-warn">{t.notConfigured}</p> : !v.folderOn ? <p class="small contract-warn">{t.noFolder}</p> : null}
        <p class="small muted">{t.counts(v.counts)}</p>
      </Block>
      <Block title={t.status.proposed}>
        {v.toReview.length ? (
          <ul class="terms-list">
            {v.toReview.map((r, i) => (
              <li key={r.id} class="terms-row">
                <span>
                  <strong>{r.clientName}</strong>
                  {!r.clientActive ? <span class="muted"> · {t.inactive}</span> : null}
                  <span class="cell-sub">
                    {r.fileName}
                    {r.fileDate ? ` · ${day(r.fileDate, lang)}` : ''} · {r.status === 'failed' ? errorText(r.error, lang) : t.found(r.found)}
                  </span>
                </span>
                <Pill tone={tone(r.status)}>{t.status[r.status]}</Pill>
                <button type="button" class="btn btn-sm" onClick={() => setOpen(i)}>
                  {t.review}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p class="muted small">{t.nothing}</p>
        )}
      </Block>
      {open !== null ? <TermsDialog api={p.api} rows={v.toReview} start={open} standard={v.standard} onDone={() => void load()} onClose={() => setOpen(null)} /> : null}
    </>
  );
}

type Form = Record<Exclude<CostKey, 'travel' | 'packages'>, string> & {
  travel: TravelRule | null;
  packages: AgreementPackagePrice[] | null;
};
const formOf = (c: AgreementCosts | null): Form => Object.fromEntries(COST_KEYS.map((k) => [k, k === 'travel' || k === 'packages' ? (c?.[k] ?? null) : c?.[k] === null || c?.[k] === undefined ? '' : String(c[k])])) as Form;
const costsOf = (f: Form): Record<string, unknown> => Object.fromEntries(COST_KEYS.map((k) => [k, k === 'travel' || k === 'packages' ? f[k] : f[k].trim() === '' ? null : f[k]]));
const TEXT_KEYS: CostKey[] = ['packageKey', 'untilDate', 'other', 'travel'];
/** The form as figures, to compare with the price list while the reviewer types. */
function parsed(f: Form): AgreementCosts {
  const out = Object.fromEntries(
    COST_KEYS.map((k) => {
      if (k === 'travel' || k === 'packages') return [k, f[k]];
      const s = f[k].trim();
      if (!s) return [k, null];
      if (TEXT_KEYS.includes(k)) return [k, s];
      const n = Number(s.replace(/\s/g, '').replace(',', '.'));
      return [k, Number.isFinite(n) ? n : s];
    })
  );
  return out as unknown as AgreementCosts;
}
const PILL_TONE: Record<CostStatus, 'ok' | 'warn' | 'info' | 'neutral'> = {
  standard: 'ok',
  differs: 'warn',
  found: 'info',
  missing: 'neutral'
};
export const labelOf = (k: CostKey, lang: Lang) => COST_LABELS[k][lang].replace(/ \(.*\)$/, '').replace(/, gånger timpriset|, times the hourly price/, '');

/** One field's input; travel has its own three parts. */
function CostInput(p: { k: CostKey; f: Form; setF: (f: Form) => void }) {
  const lang = useLang();
  const t = TT[lang];
  const { k, f, setF } = p;
  const tr = f.travel;
  const setTravel = (patch: Partial<TravelRule> | null) =>
    setF({
      ...f,
      travel: patch === null ? null : { ...(tr ?? STANDARD_TRAVEL), ...patch }
    });
  if (k === 'travel')
    return (
      <div class="cluster">
        <select
          class="input"
          id="tf-travel"
          value={tr ? tr.included : ''}
          onChange={(e) => {
            const v = (e.target as HTMLSelectElement).value;
            setTravel(v ? { included: v as TravelIncluded } : null);
          }}
        >
          <option value="">{t.notFound}</option>
          {(['stockholm', 'all', 'none'] as const).map((x) => (
            <option value={x}>{t.travel[x]}</option>
          ))}
        </select>
        {tr && tr.included !== 'all' ? (
          <>
            <input
              class="input input--num"
              type="number"
              min="0"
              max="100"
              step="5"
              aria-label={t.share}
              title={t.share}
              value={Math.round(tr.timeShare * 100)}
              onInput={(e) =>
                setTravel({
                  timeShare: Math.min(1, Math.max(0, Number((e.target as HTMLInputElement).value) / 100))
                })
              }
            />
            <input
              class="input input--num"
              type="number"
              min="0"
              step="50"
              aria-label={t.ownRate}
              title={t.ownRate}
              placeholder={t.ownRate}
              value={tr.hourlyRate ?? ''}
              onInput={(e) =>
                setTravel({
                  hourlyRate: Number((e.target as HTMLInputElement).value) || null
                })
              }
            />
          </>
        ) : null}
      </div>
    );
  if (k === 'packages') {
    const list = f.packages ?? [];
    const setList = (next: AgreementPackagePrice[]) => setF({ ...f, packages: next.length ? next : null });
    return (
      <div class="terms-pkgs">
        {list.map((x, i) => (
          <div class="cluster" key={i}>
            <input class="input" aria-label={t.pkgName} value={x.key} onInput={(e) => setList(list.map((y, j) => (j === i ? { ...y, key: (e.target as HTMLInputElement).value } : y)))} />
            <input
              class="input input--num"
              inputMode="decimal"
              aria-label={t.pkgPrice}
              value={x.price || ''}
              onInput={(e) =>
                setList(
                  list.map((y, j) =>
                    j === i
                      ? {
                          ...y,
                          price: Number((e.target as HTMLInputElement).value.replace(/\s/g, '')) || 0
                        }
                      : y
                  )
                )
              }
            />
            <button type="button" class="btn btn-ghost btn-sm" onClick={() => setList(list.filter((_, j) => j !== i))}>
              {t.pkgRemove}
            </button>
          </div>
        ))}
        <button type="button" class="btn btn-ghost btn-sm" onClick={() => setList([...list, { key: '', price: 0 }])}>
          {t.pkgAdd}
        </button>
      </div>
    );
  }
  if (k === 'other') return <textarea class="textarea" id="tf-other" rows={2} value={f.other} onInput={(e) => setF({ ...f, other: (e.target as HTMLTextAreaElement).value })} />;
  return (
    <input
      class={k === 'packageKey' ? 'input' : k === 'untilDate' ? 'input input--date' : 'input input--num'}
      id={`tf-${k}`}
      type={k === 'untilDate' ? 'date' : 'text'}
      inputMode={k === 'packageKey' || k === 'untilDate' ? undefined : 'decimal'}
      placeholder={t.none}
      value={f[k]}
      onInput={(e) => setF({ ...f, [k]: (e.target as HTMLInputElement).value })}
    />
  );
}

/**
 * One agreement at a time, in three plain groups (what the client pays, has committed to, and pays extra for). Each figure
 * says whether it is as the price list, differs from it, or is only in the agreement; a click opens it with the sentence it
 * came from. What the agreement does not mention is one folded line. "Correct" approves and moves to the next agreement.
 */
export function TermsDialog(p: { api: Api; rows: TermsRow[]; start: number; standard: AgreementCosts; onDone: (r: TermsRow) => void; onClose: () => void }) {
  const lang = useLang();
  const t = TT[lang];
  const [i, setI] = useState(p.start);
  const row = p.rows[i]!;
  const [f, setF] = useState<Form>(formOf(row.costs));
  const [editing, setEditing] = useState<CostKey[]>([]);
  const [more, setMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const goTo = (n: number) => {
    const r = p.rows[n];
    if (!r) return p.onClose();
    setI(n);
    setF(formOf(r.costs));
    setEditing([]);
    setMore(false);
  };
  const run = async (path: string, body: unknown, done: string) => {
    setBusy(true);
    setErr(null);
    try {
      const r = await p.api.post<{ terms: TermsRow }>(`/api/agreement-terms/${row.id}/${path}`, body);
      toast({ text: done });
      p.onDone(r.terms);
      if (i + 1 < p.rows.length) goTo(i + 1);
      else p.onClose();
    } catch (e) {
      setErr(msg(e));
    } finally {
      setBusy(false);
    }
  };
  const c = parsed(f);
  const open = (k: CostKey) => setEditing(editing.includes(k) ? editing.filter((x) => x !== k) : [...editing, k]);
  const silent = COST_KEYS.filter((k) => costStatus(k, c, p.standard) === 'missing' && !editing.includes(k));
  const isLast = i + 1 >= p.rows.length;
  return (
    <Dialog
      title={row.clientName}
      sub={
        <span class="cluster small">
          {p.rows.length > 1 ? <span class="muted">{t.progress(i + 1, p.rows.length)}</span> : null}
          {row.url ? (
            <a href={row.url} target="_blank" rel="noopener noreferrer">
              {t.open}
            </a>
          ) : (
            <span class="muted">{row.fileName}</span>
          )}
        </span>
      }
      onClose={p.onClose}
      onSubmit={() => void run('approve', { costs: costsOf(f) }, t.approved)}
      foot={
        <>
          <span class="terms-more">
            <button type="button" class="btn btn-ghost" aria-expanded={more} onClick={() => setMore(!more)}>
              {t.more}
            </button>
            {more ? (
              <>
                <button type="button" class="btn btn-ghost" disabled={busy} onClick={() => void run('reject', {}, t.rejected)}>
                  {t.reject}
                </button>
                {row.status === 'failed' || row.status === 'proposed' ? (
                  <button type="button" class="btn btn-ghost" disabled={busy} onClick={() => void run('rescan', {}, t.rescanned)}>
                    {t.rescan}
                  </button>
                ) : null}
              </>
            ) : null}
          </span>
          <button type="submit" class="btn btn-primary" disabled={busy}>
            {isLast ? t.last : t.next}
          </button>
        </>
      }
    >
      {row.summary ? (
        <p class="terms-summary small">
          <strong>{t.klara}:</strong> {row.summary}
        </p>
      ) : null}
      {row.error ? <p class="small contract-warn">{errorText(row.error, lang)}</p> : null}
      <p class="small muted">{t.clickHint}</p>
      {COST_GROUPS.map((g) => {
        const keys = g.keys.filter((k) => !silent.includes(k));
        if (!keys.length) return null;
        return (
          <section class="terms-group" key={g.key}>
            <h3 class="terms-group-h">{g[lang]}</h3>
            <ul class="terms-rows">
              {keys.map((k) => {
                const st = costStatus(k, c, p.standard);
                const on = editing.includes(k);
                const q = row.quotes[k];
                return (
                  <li key={k} class={`terms-line${on ? ' is-open' : ''}`}>
                    <button type="button" class="terms-line-btn" aria-expanded={on} onClick={() => open(k)}>
                      <span class="terms-line-label">{labelOf(k, lang)}</span>
                      <span class="terms-line-val">{cardText(k, c, lang)}</span>
                      <Pill tone={PILL_TONE[st]}>{t.pill[st]}</Pill>
                    </button>
                    {st === 'differs' ? <p class="terms-line-note small">{t.priceSays(cardText(k, p.standard, lang))}</p> : null}
                    {on ? (
                      <div class="terms-line-edit">
                        <label class="sr-only" for={`tf-${k}`}>
                          {labelOf(k, lang)}
                        </label>
                        <CostInput k={k} f={f} setF={setF} />
                        {q ? (
                          <p class="terms-quote">
                            <span class="muted">{t.fromAgreement}</span> ”{q}”
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
      {silent.length ? (
        <p class="small muted terms-silent">
          {t.silent}{' '}
          {silent.map((k, n) => (
            <>
              <button type="button" class="linkish" onClick={() => open(k)}>
                {labelOf(k, lang).toLowerCase()}
              </button>
              {n < silent.length - 1 ? ', ' : '. '}
            </>
          ))}
          {t.silentEnd}
        </p>
      ) : null}
      {err ? (
        <p class="field-error" role="alert">
          {err}
        </p>
      ) : null}
    </Dialog>
  );
}

interface ClientTermsView {
  applies: ClientCostsView & { approvedAt: string | null };
  standard: AgreementCosts;
  commitment: {
    min: number;
    done: number | null;
    start: string | null;
    end: string | null;
  } | null;
  agreements: TermsRow[];
  canApprove: boolean;
}

/** A figure on the client card: overtime as kronor an hour, the factor after it. */
function cardText(k: CostKey, c: AgreementCosts, lang: Lang): string {
  if (k === 'overtimeFactor' && c.overtimeFactor && c.hourlyRate) return `${kr(Math.round(c.hourlyRate * c.overtimeFactor))}/h ${TT[lang].perHour(`${String(c.overtimeFactor).replace('.', ',')} ×`)}`;
  return costText(k, c, lang);
}

/**
 * The client card, in the order a producer asks: what the client has (one sentence), how far it has come on its
 * commitment, and what costs extra in kronor. A yellow dot marks a figure that differs from today's price list. The rest
 * is under "Show all terms"; the source and the review are at the foot.
 */
export function ClientTermsBlock(p: { api: Api; clientId: string }) {
  const lang = useLang();
  const t = TT[lang];
  const [v, setV] = useState<ClientTermsView | null>(null);
  const [open, setOpen] = useState<TermsRow | null>(null);
  const [all, setAll] = useState(false);
  const load = () => p.api.get<ClientTermsView>(`/api/clients/${p.clientId}/terms`).then(setV, () => setV(null));
  useEffect(() => void load(), [p.clientId]);
  if (!v) return null;
  const a = v.applies;
  const c = a.costs;
  const own = a.source !== 'standard';
  const differs = (k: CostKey) => own && a.from[k] === 'agreement' && costStatus(k, c, v.standard) === 'differs';
  const head = [c.packageKey, c.price ? kr(c.price) : null, c.untilDate ? t.until(day(c.untilDate, lang)) : c.periodMonths ? t.length(c.periodMonths) : null].filter(Boolean).join(' · ');
  const cm = v.commitment;
  // the agreement's own figures up front, the price list's under "Show all terms"
  const extra = COST_GROUPS.find((g) => g.key === 'extra')!.keys.filter((k) => c[k] !== null && (!own || a.from[k] === 'agreement' || k === 'hourlyRate'));
  const shown = all ? COST_KEYS.filter((k) => c[k] !== null && !extra.includes(k)) : [];
  const line = (k: CostKey) => (
    <>
      <dt>{labelOf(k, lang)}</dt>
      <dd>
        {cardText(k, c, lang)}
        {differs(k) ? (
          <span class="terms-diff" title={t.priceSays(cardText(k, v.standard, lang))}>
            {' '}
            <span class="dot dot--warn" aria-hidden="true" /> <span class="muted">{t.priceSays(cardText(k, v.standard, lang))}</span>
          </span>
        ) : null}
      </dd>
    </>
  );
  const review = v.canApprove ? (v.agreements.find((r) => r.status === 'approved' && r.fileName === a.ref) ?? v.agreements.find((r) => r.status !== 'queued')) : undefined;
  return (
    <div class="bill-terms">
      <p class="small">
        <strong>{t.cardHead}</strong>
        {own && head ? <span> · {head}</span> : null}
      </p>
      {cm ? (
        cm.min === 0 ? (
          <p class="small muted">{t.frame}</p>
        ) : (
          <div class="terms-meter small">
            <p>{cm.done === null ? t.minimum(cm.min, c.periodMonths) : t.called(cm.done, cm.min)}</p>
            {cm.done !== null ? (
              <>
                <div class="terms-bar" role="progressbar" aria-valuemin={0} aria-valuemax={cm.min} aria-valuenow={cm.done}>
                  <span
                    style={{
                      width: `${Math.min(100, Math.round((cm.done / cm.min) * 100))}%`
                    }}
                  />
                </div>
                <p class="muted">{cm.done >= cm.min ? t.reached : `${cm.end ? `${t.left(cm.min - cm.done, day(cm.end, lang))}. ` : ''}${t.shortfall}`}</p>
              </>
            ) : null}
          </div>
        )
      ) : null}
      {extra.length ? (
        <>
          <p class="small muted terms-sub">{t.extra}</p>
          <dl class="bill-dl small">{extra.map(line)}</dl>
        </>
      ) : null}
      {shown.length ? <dl class="bill-dl small">{shown.map(line)}</dl> : null}
      <p class="small muted cluster">
        <span>{a.source === 'quote' && a.ref ? t.srcQuote(a.ref) : a.source === 'scan' && a.ref ? t.srcScan(a.ref) : t.srcStandard}</span>
        {a.approvedAt ? <span>· {t.approvedOn(day(a.approvedAt, lang))}</span> : null}
        <button type="button" class="linkish" onClick={() => setAll(!all)}>
          {all ? t.showLess : t.showAll}
        </button>
        {review ? (
          <button type="button" class="linkish" onClick={() => setOpen(review)}>
            {t.change}
          </button>
        ) : null}
      </p>
      {all && v.agreements.length ? (
        <>
          <p class="small muted">{t.read}</p>
          <ul class="terms-list small">
            {v.agreements.map((r) => (
              <li key={r.id} class="terms-row">
                <span>
                  {r.fileName}
                  {r.fileDate ? <span class="muted"> · {day(r.fileDate, lang)}</span> : null}
                </span>
                <Pill tone={tone(r.status)}>{t.status[r.status]}</Pill>
                {v.canApprove && r.status !== 'queued' ? (
                  <button type="button" class="btn btn-ghost btn-sm" onClick={() => setOpen(r)}>
                    {t.review}
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {open ? <TermsDialog api={p.api} rows={[open]} start={0} standard={v.standard} onDone={() => void load()} onClose={() => setOpen(null)} /> : null}
    </div>
  );
}

/** The basis's quick lines, priced from the client's agreement (the standard where it says nothing). */
export function quickLines(c: AgreementCosts, lang: Lang): { label: string; line: BasisLine }[] {
  const sv = lang === 'sv';
  const base = { key: '', source: 'manual' as const, vat: 25 as const };
  const out: { label: string; line: BasisLine }[] = [];
  if (c.hourlyRate)
    out.push({
      label: sv ? 'Extra tid' : 'Extra time',
      line: {
        ...base,
        text: 'Extra tid (inspelning, redigering, admin)',
        qty: 1,
        unit: 'h',
        unitPrice: c.hourlyRate
      }
    });
  if (c.hourlyRate && c.overtimeFactor)
    out.push({
      label: sv ? 'Övertid' : 'Overtime',
      line: {
        ...base,
        text: 'Övertid kvällar och helger',
        qty: 1,
        unit: 'h',
        unitPrice: Math.round(c.hourlyRate * c.overtimeFactor)
      }
    });
  if (c.rescheduleFee)
    out.push({
      label: sv ? 'Ombokning' : 'Rescheduling',
      line: {
        ...base,
        text: 'Ombokning mindre än 7 dagar före inspelning',
        qty: 1,
        unit: 'once',
        unitPrice: c.rescheduleFee
      }
    });
  if (c.expressPrice)
    out.push({
      label: sv ? 'Express' : 'Express',
      line: {
        ...base,
        text: 'Expressleverans',
        qty: 1,
        unit: 'once',
        unitPrice: c.expressPrice
      }
    });
  return out;
}
